// PropertyCard.jsx — Society-level card (Zolo-style)
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaMapMarkerAlt, FaBed, FaArrowRight, FaBuilding } from 'react-icons/fa';

const PropertyCard = ({ property }) => {
  const [imgError, setImgError] = useState(false);

  // Works with both grouped society objects and legacy flat property objects
  const isSociety = !!property.flatTypes;

  const mainImage =
    (!imgError && (property.displayImage || (property.images && property.images[0]))) ||
    'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?auto=format&fit=crop&q=80&w=600';

  const societyName = property.societyName || property.title;
  const location = property.location;
  const slug = property.societySlug || property.id;
  const googleRating = property.googleRating || '';

  // Flat type badges (2BHK, 3BHK …)
  const flatTypeBadges = isSociety
    ? property.displayFlatTypes
    : property.flatType ? [property.flatType] : [];

  // Starting price
  const startPrice = isSociety ? property.minPrice : property.price;

  // Room type preview — collect all room types across flat types
  const roomTypes = isSociety
    ? [...new Set(
        Object.values(property.flatTypes).flatMap(ft =>
          ft.roomOptions.map(r => r.roomType)
        )
      )]
    : property.roomType ? [property.roomType] : [];

  const totalRooms = isSociety ? property.roomCount : 1;

  return (
    <div className="card group relative flex flex-col overflow-hidden rounded-2xl shadow-soft hover:shadow-xl transition-shadow duration-300 bg-white">
      <Link to={`/property/${slug}`} className="flex flex-col h-full">
        {/* Image */}
        <div className="relative overflow-hidden aspect-[4/3] shrink-0">
          <img
            src={mainImage}
            alt={societyName}
            onError={() => setImgError(true)}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-in-out"
          />
          {/* Availability badge */}
          <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
            {flatTypeBadges.map(ft => (
              <span
                key={ft}
                className="backdrop-blur-sm bg-primary-600/90 text-white text-[10px] font-bold px-2.5 py-1 rounded-full tracking-wider uppercase shadow"
              >
                {ft}
              </span>
            ))}
          </div>

          {/* Rating */}
          {googleRating && (
            <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-secondary-800 px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow">
              {googleRating}
            </div>
          )}

          {/* Rooms count pill */}
          <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1">
            <FaBed className="text-primary-300 text-xs" />
            {totalRooms} room{totalRooms !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Content */}
        <div className="p-5 flex flex-col flex-1 gap-3">
          {/* Name + Price */}
          <div className="flex justify-between items-start gap-2">
            <h3 className="text-base font-heading font-bold text-secondary-900 group-hover:text-primary-600 transition-colors leading-snug line-clamp-2">
              {societyName}
            </h3>
            <div className="text-right shrink-0">
              <div className="text-primary-600 font-extrabold text-lg leading-none">
                <span className="text-xs font-normal text-gray-400">₹</span>
                {startPrice?.toLocaleString('en-IN')}
              </div>
              <div className="text-[10px] text-gray-400 mt-0.5">/ month</div>
            </div>
          </div>

          {/* Location */}
          <div className="flex items-center text-secondary-500 text-sm gap-1.5">
            <FaMapMarkerAlt className="text-primary-400 shrink-0 text-xs" />
            <span className="truncate">{location}</span>
          </div>

          {/* Room type pills */}
          {roomTypes.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {roomTypes.slice(0, 3).map(rt => (
                <span
                  key={rt}
                  className="text-[10px] px-2 py-0.5 bg-secondary-50 text-secondary-600 rounded-full border border-secondary-100 font-medium"
                >
                  {rt}
                </span>
              ))}
              {roomTypes.length > 3 && (
                <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full font-medium">
                  +{roomTypes.length - 3} more
                </span>
              )}
            </div>
          )}

          {/* Footer CTA */}
          <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-secondary-500 text-xs">
              <FaBuilding className="text-primary-400 text-sm" />
              <span>Women Only PG</span>
            </div>
            <span className="flex items-center gap-1 text-primary-600 font-semibold text-xs group-hover:gap-2 transition-all">
              View Rooms <FaArrowRight className="text-[10px]" />
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default PropertyCard;